<script lang="ts" setup>
import InputText from 'primevue/inputtext';
import Message from 'primevue/message';
import type { FloatLabelVariant } from '~/types/components/base/FloatLabel';
import type {
  TextInputSize,
  TextInputVariant,
} from '~/types/components/base/TextInput';
import { IN } from '~/utils/enums/components/base/FloatLabel';
import AppFloatingLabelWrapper from './AppFloatingLabelWrapper.vue';

defineOptions({
  inheritAttrs: false,
});

/**
 * https://primevue.org/inputtext/#api.inputtext.props
 */
const {
  placeholder = undefined,
  label = undefined,
  variant = undefined,
  size = undefined,
  errorMessage = undefined,

  floatingLabel = true,
  floatingLabelVariant = IN,
  disabled = false,
  loading = false,
  invalid = false,
} = defineProps<{
  placeholder?: string;
  label?: string;
  variant?: TextInputVariant;
  size?: TextInputSize;
  errorMessage?: string;

  floatingLabel?: boolean;
  floatingLabelVariant?: FloatLabelVariant;
  disabled?: boolean;
  loading?: boolean;
  invalid?: boolean;
}>();
</script>

<template>
  <AppFloatingLabelWrapper
    :floating-label="floatingLabel"
    :floating-label-variant="floatingLabelVariant"
    :label="label"
  >
    <template #default="{ labelId: id }">
      <InputText
        :id="id"
        :variant="variant"
        :size="size"
        :disabled="disabled"
        :loading="loading"
        :invalid="invalid"
        :label="floatingLabel ? undefined : label"
        :placeholder="floatingLabel ? undefined : placeholder"
        class="app-input-text"
        v-bind="$attrs"
      />
    </template>
  </AppFloatingLabelWrapper>

  <Message
    v-if="!!errorMessage"
    severity="error"
    size="small"
    variant="simple"
    class="mt-2"
    >{{ errorMessage }}</Message
  >
</template>

<style lang="scss" scoped>
@use '../../assets/scss/partials/spacing' as *;

// VTW-484: iOS Safari auto-zooms the page when a focused input's computed
// font-size is under 16px — every search field on mobile is an AppTextInput
// at `size="small"`, whose 14px theme token triggers it. The shared
// `.p-inputtext` rule in assets/scss/components/_input.scss already covers
// this, but that fix already broke once in production purely from CSS load
// order (see its comment there). Chaining onto `.p-inputtext` here — rather
// than relying on `.app-input-text` alone, which ties that shared rule's
// specificity — is this component's own second line of defense, scoped to
// its own stylesheet so it can't lose that tie regardless of import order.
// The threshold and safe font-size are shared with that rule via
// partials/spacing so the two copies can't drift apart.
.app-input-text.p-inputtext {
  @media (max-width: $breakpoint-mobile-zoom-max) {
    font-size: $mobile-zoom-safe-font-size;
  }
}
</style>
